import { useState, useEffect, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useTabParam } from "../../lib/useTabParam";
import type { TeamRole } from "../../lib/types";
import TodayTab from "./TodayTab";
import CalendarTab from "./CalendarTab";
import PreInspectionsTab from "./PreInspectionsTab";
import ScheduleTab from "./ScheduleTab";
import PMBoardTab from "./PMBoardTab";
import ChadApprovalsTab from "./ChadApprovalsTab";
import ChadSLATab from "./ChadSLATab";
import ChadFollowupsTab from "./ChadFollowupsTab";
import ExecOverviewTab from "./ExecOverviewTab";
import PunchListTab from "./PunchListTab";
import WarrantyTab from "./WarrantyTab";

/**
 * Dispatch Board — role-smart view with switchable role bar.
 *
 * Role views:
 *   Robert (Roof Team Manager): Today, Build Approvals, Schedule, PM Board, Punch List, Warranty
 *   Roof Specialist (per-RS):   Today, My Schedule, Pre-Inspections  (one board each:
 *                               Maverick, Sam, …). Each RS is locked to their own board;
 *                               managers/execs can open any via the pill bar or Home tile.
 *   Jennifer (QC/Director):     Today, Calendar, Punch List, Warranty
 *   Chad (Sales Director):      Build Approvals, Build Follow-ups, Punch List
 *   Build Approvals (Oct 22, 2026) is the one route a submitted build takes;
 *   Chad or Robert approves there, so it sits on both their views.
 *   Executive (Admin/ASM):      Executive Overview + all tabs (incl. Warranty)
 *   Trades moved to the Builds board (Build Calendar, ?view=trades) on Oct 28, 2026; the tab here never held a row.
 *
 * RS views are keyed `rs:<team_roster.id>` so the board knows which specialist's
 * stops to tag as "YOU". Static views keep their literal keys.
 */

// ── View-as role options ──
type StaticView = "robert" | "jennifer" | "chad" | "executive";
type ViewAsKey = StaticView | `rs:${string}`;

const isRsView = (v: string): v is `rs:${string}` => v.startsWith("rs:");
const rsIdOf = (v: string): string | undefined => (isRsView(v) ? v.slice(3) : undefined);

type ViewOption = { key: ViewAsKey; label: string; emoji: string };

type TabConfig = {
  key: string;
  label: string;
};

function getTabsForView(view: ViewAsKey): TabConfig[] {
  if (isRsView(view)) {
    return [
      { key: "today", label: "Today" },
      { key: "calendar", label: "My Schedule" },
      { key: "preinspections", label: "Pre-Inspections" },
    ];
  }
  switch (view) {
    case "robert":
      return [
        { key: "today", label: "My Stops Today" },
        { key: "approvals", label: "Build Approvals" },
        { key: "unscheduled", label: "Schedule" },
        { key: "preinspections", label: "Pre-Inspections" },
        { key: "pm", label: "PM Board" },
        { key: "punchlist", label: "Punch List" },
        { key: "warranty", label: "Warranty" },
      ];
    case "jennifer":
      return [
        { key: "today", label: "My Stops Today" },
        { key: "calendar", label: "My Schedule" },
        { key: "preinspections", label: "Pre-Inspections" },
        { key: "punchlist", label: "Punch List" },
        { key: "warranty", label: "Warranty" },
      ];
    case "chad":
      return [
        { key: "approvals", label: "Build Approvals" },
        { key: "followups", label: "Build Follow-ups" },
        { key: "punchlist", label: "Punch List" },
      ];
    case "executive":
    default:
      return [
        { key: "overview", label: "Executive Overview" },
        { key: "today", label: "Today" },
        { key: "unscheduled", label: "Schedule" },
        { key: "pm", label: "PM Board" },
        { key: "approvals", label: "Build Approvals" },
        { key: "sla", label: "SLA Issues" },
        { key: "followups", label: "Build Follow-ups" },
        { key: "punchlist", label: "Punch List" },
        { key: "warranty", label: "Warranty" },
      ];
  }
}

/** Map the logged-in user's TeamRole to their default static view. */
function defaultStaticViewForRole(role: TeamRole | undefined): StaticView {
  switch (role) {
    case "roof_team_manager":
      return "robert";
    case "qc":
    case "roof_director":
    case "director_of_operations":
      return "jennifer";
    case "sales_director":
      return "chad";
    default:
      return "executive";
  }
}

export default function DispatchBoard() {
  const { role: userRole, member } = useAuth();

  // Active roof specialists drive one board (and one pill) each.
  const [rsRoster, setRsRoster] = useState<Array<{ id: string; full_name: string }>>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("team_roster")
        .select("id,full_name")
        .eq("role", "roof_specialist")
        .eq("is_active", true)
        .order("full_name");
      if (!cancelled && data) setRsRoster(data as Array<{ id: string; full_name: string }>);
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve the person behind the static person-pills (Robert = sole
  // roof_team_manager, Jennifer = sole director_of_operations) so their boards
  // highlight their own assigned stops / pre-inspections — same "YOU" treatment
  // each RS pill already gets. Both can be the assigned RS on a meeting
  // (deals.assigned_rs_id), so they have real jobs to flag.
  const [robertRow, setRobertRow] = useState<{ id: string; full_name: string } | null>(null);
  const [jenniferRow, setJenniferRow] = useState<{ id: string; full_name: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("team_roster")
        .select("id,full_name,role")
        .in("role", ["roof_team_manager", "director_of_operations"])
        .eq("is_active", true)
        .order("full_name");
      if (cancelled || !data) return;
      const rows = data as Array<{ id: string; full_name: string; role: string }>;
      setRobertRow(rows.find((r) => r.role === "roof_team_manager") ?? null);
      setJenniferRow(rows.find((r) => r.role === "director_of_operations") ?? null);
    })();
    return () => { cancelled = true; };
  }, []);

  // Merge in the logged-in RS's own row so their board resolves immediately,
  // before the roster fetch returns (so their pill + name never flicker).
  const rsList = useMemo(() => {
    const map = new Map<string, string>();
    rsRoster.forEach((r) => map.set(r.id, r.full_name));
    if (userRole === "roof_specialist" && member) map.set(member.id, member.full_name);
    return [...map.entries()]
      .map(([id, full_name]) => ({ id, full_name }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [rsRoster, userRole, member]);

  // Full pill list: Robert, [one per RS], Jennifer, Chad, Executive.
  const viewOptions = useMemo<ViewOption[]>(() => {
    const rsPills: ViewOption[] = rsList.map((r) => ({
      key: `rs:${r.id}` as ViewAsKey,
      emoji: "🏗️",
      label: `${r.full_name.split(" ")[0]} (RS)`,
    }));
    return [
      { key: "robert", emoji: "🔧", label: "Robert (PM)" },
      ...rsPills,
      { key: "jennifer", emoji: "🔍", label: "Jennifer (QC)" },
      { key: "chad", emoji: "👔", label: "Chad (Director)" },
      { key: "executive", emoji: "📊", label: "Executive" },
    ];
  }, [rsList]);

  const myDefault: ViewAsKey = useMemo(
    () =>
      userRole === "roof_specialist" && member
        ? (`rs:${member.id}` as ViewAsKey)
        : defaultStaticViewForRole(userRole),
    [userRole, member],
  );

  // viewAs + tab are URL-backed so back-navigation from a deal returns here
  // exactly as you left it (e.g. Schedule tab on the Robert view).
  // roof_specialists are locked to their own board — no switching to other
  // people's views, including via the ?view= URL param. Restricting the
  // valid-key allowlist makes useTabParam fall back to myDefault for any other
  // value, which closes off URL manipulation for free.
  const allowedViews: ViewAsKey[] = useMemo(
    () => (userRole === "roof_specialist" ? [myDefault] : viewOptions.map((o) => o.key)),
    [userRole, myDefault, viewOptions],
  );
  const [viewAs, setViewAs] = useTabParam<ViewAsKey>("view", myDefault, allowedViews);
  // Pill bar shows only the user's own pill when locked to one view.
  const visibleViews =
    userRole === "roof_specialist"
      ? viewOptions.filter((o) => o.key === myDefault)
      : viewOptions;

  // The person this board represents — drives the "YOU" badge + "Yours" KPI on
  // Today and Pre-Inspections for stops/jobs assigned to them. Covers every RS
  // pill plus the Robert and Jennifer person-pills (they can be the assigned RS
  // on a meeting too). Chad / Executive are role views with no single owner.
  const { id: rsId, name: rsName } = useMemo<{ id?: string; name?: string }>(() => {
    if (isRsView(viewAs)) {
      const id = rsIdOf(viewAs);
      return { id, name: id ? rsList.find((r) => r.id === id)?.full_name ?? member?.full_name : undefined };
    }
    if (viewAs === "robert")
      return { id: robertRow?.id, name: robertRow?.full_name };
    if (viewAs === "jennifer")
      return { id: jenniferRow?.id, name: jenniferRow?.full_name };
    return {};
  }, [viewAs, rsList, member, robertRow, jenniferRow]);

  const tabs = useMemo(() => getTabsForView(viewAs), [viewAs]);
  const validTabKeys = useMemo(() => tabs.map((t) => t.key), [tabs]);
  const defaultTabKey = tabs[0]?.key ?? "today";
  const [tab, setTab] = useTabParam<string>("tab", defaultTabKey, validTabKeys);

  // When viewAs changes, reset to first tab of new view
  const handleViewChange = (newView: ViewAsKey) => {
    setViewAs(newView);
    const newTabs = getTabsForView(newView);
    setTab(newTabs[0]?.key ?? "today");
    setPmDealId(null);
  };

  // Badge counts for tabs (lightweight count queries)
  const [badges, setBadges] = useState<Record<string, number>>({});

  const loadBadges = useCallback(async () => {
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const dayStart = `${today}T00:00:00Z`;
      const dayEnd = `${today}T23:59:59Z`;

      const [r1, r2, r3, r4, r5] = await Promise.all([
        supabase.from("deals").select("*", { count: "exact", head: true })
          .gte("adjuster_meeting_date", dayStart).lte("adjuster_meeting_date", dayEnd),
        supabase.from("deals").select("*", { count: "exact", head: true })
          .gte("pre_inspection_date", dayStart).lte("pre_inspection_date", dayEnd),
        supabase.from("deals").select("*", { count: "exact", head: true })
          .eq("build_scheduled_date", today),
        supabase.from("deals").select("*", { count: "exact", head: true })
          .eq("stage", "build_submitted"),
        supabase.from("deals").select("*", { count: "exact", head: true })
          .eq("phase", "claims").is("pre_inspection_date", null),
      ]);

      setBadges({
        today: (r1.count ?? 0) + (r2.count ?? 0) + (r3.count ?? 0),
        approvals: r4.count ?? 0,
        sla: r5.count ?? 0,
      });
    } catch {
      // Badge loading is non-critical
    }
  }, []);

  useEffect(() => { loadBadges(); }, [loadBadges]);

  // PM Board deal pre-selection
  const [pmDealId, setPmDealId] = useState<string | null>(null);

  const handleOpenPMBoard = (dealId: string) => {
    setPmDealId(dealId);
    setTab("pm");
  };

  // Subtitle per view
  const subtitle = useMemo(() => {
    if (isRsView(viewAs)) {
      const first = (rsName ?? "This specialist").split(" ")[0];
      return `${first}'s board · adjuster meetings, pre-inspections, and builds. Open Pre-Inspections to batch nearby climbs by location.`;
    }
    switch (viewAs) {
      case "robert": return "Approve submitted builds, then schedule them · adjuster meetings are hubs, pre-inspections are spokes.";
      case "jennifer": return "QC reviews, reinspections, and warranty visits.";
      case "chad": return "Build approvals (Chad or Robert approves here) and post-build follow-up visits.";
      case "executive": return "KPIs, RS workload, and scheduling pipeline health.";
    }
  }, [viewAs, rsName]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-3">
        <h1 className="text-2xl font-semibold text-bardin-ink">Dispatch Board</h1>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
      </div>

      {/* Role bar — matches Drone Ops C3 pattern. roof_specialist sees only
          their own pill (no view switching). */}
      <div className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 mb-5 overflow-x-auto whitespace-nowrap flex gap-1.5">
        {visibleViews.map((opt) => (
          <button
            key={opt.key}
            onClick={() => handleViewChange(opt.key)}
            className={
              "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition flex-shrink-0 " +
              (viewAs === opt.key
                ? "bg-bardin-teal text-white border-bardin-teal shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
            }
          >
            <span>{opt.emoji}</span>
            {opt.label}
            {opt.key === myDefault ? " (You)" : ""}
          </button>
        ))}
      </div>

      {/* Tabs for selected view */}
      {tabs.length > 1 && (
        <div className="border-b-2 border-slate-200 mb-5">
          <nav className="flex gap-1 flex-wrap" aria-label="Dispatch tabs">
            {tabs.map((t) => {
              const badge = badges[t.key] ?? 0;
              return (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); if (t.key !== "pm") setPmDealId(null); }}
                  className={
                    "inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold -mb-px border-b-2 whitespace-nowrap transition-all " +
                    (tab === t.key
                      ? "text-teal-500 border-teal-500"
                      : "text-slate-500 border-transparent hover:text-slate-700")
                  }
                >
                  {t.label}
                  {badge > 0 && (
                    <span className="bg-red-500 text-white text-[9px] font-extrabold min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full leading-none">
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      <div>
        {tab === "today" && <TodayTab highlightRsId={rsId} />}
        {tab === "calendar" && <CalendarTab highlightRsId={rsId} />}
        {tab === "preinspections" && <PreInspectionsTab rsId={rsId} rsName={rsName} />}
        {tab === "unscheduled" && <ScheduleTab onOpenPMBoard={handleOpenPMBoard} />}
        {tab === "pm" && <PMBoardTab initialDealId={pmDealId} onClearInitial={() => setPmDealId(null)} />}
        {tab === "approvals" && <ChadApprovalsTab />}
        {tab === "sla" && <ChadSLATab />}
        {tab === "followups" && <ChadFollowupsTab />}
        {tab === "punchlist" && <PunchListTab />}
        {tab === "warranty" && <WarrantyTab />}
        {tab === "overview" && <ExecOverviewTab />}
      </div>
    </div>
  );
}
